import * as React from 'react';
import { themr } from 'react-css-themr';
import { Button, ButtonTypes } from 'common/components/core/button';

import { dialogFactory } from 'common/components/core/dialog/Dialog';
import defaultTheme from './BreaksDialog.scss';
import { OutlineCheckbox } from 'common/components/core/form/Checkbox';
import ArrayUtil from 'common/utils/ArrayUtil';
import { BreakTypesEnum } from 'common/enums/BreakTypes';
import moment from 'moment-timezone';

const Actions = ({
    showApplyToAll,
    applyToAllLabel,
    applyToAll,
    onDismiss,
    toggleApplyToAll,
    onAccept,
    t,
    theme,
    allBreaks,
    shiftStart,
    shiftEnd,
    endDayOffset
}) => {
    const shiftEndMoment = moment(shiftEnd).add(endDayOffset, 'd');
    const breakHasError = Object.values(allBreaks).some(breakType =>
        breakType.some(breakSlot => {
            const { startShiftInstant, endShiftInstant } = breakSlot;
            //Optional chaining in fn call as plan screen objects are not flow models
            return (
                breakSlot.hasValidationErrors?.() ||
                breakSlot.validateBreakStartWithShift?.({
                    shiftStart,
                    startShiftInstant
                }) ||
                breakSlot.validateBreakEndWithShift?.({
                    shiftEnd: shiftEndMoment,
                    endShiftInstant
                })
            );
        })
    );
    return (
        <div className={theme.bottomWrap}>
            {showApplyToAll ? (
                <OutlineCheckbox
                    theme={theme}
                    label={applyToAllLabel}
                    checked={applyToAll}
                    onChange={toggleApplyToAll}
                />
            ) : (
                []
            )}
            <div className={theme.actionsWrap}>
                <Button
                    label={t('cancel')}
                    buttonType={ButtonTypes.Text}
                    uppercase
                    semibold
                    onClick={onDismiss}
                />
                <Button
                    label={t('apply')}
                    buttonType={ButtonTypes.Text}
                    uppercase
                    semibold
                    color="cool-green"
                    onClick={onAccept}
                    disabled={breakHasError}
                />
            </div>
        </div>
    );
};

const breaksDialogFactory = ({ BreakTabs }) => {
    const BreaksDialogComponent = dialogFactory({
        InnerContent: BreakTabs,
        Actions
    });
    class BreaksDialog extends React.PureComponent {
        constructor(props) {
            super(props);

            this.state = {
                activeTabIndex: 0,
                breaks: props.cloneBreaks(props.defaultBreaks),
                applyToAll: this.props.applyToAllDefault ? true : false,
                activeBreakKey: null,
                unappliedBreaks: []
            };
            this.breakTypeList = [
                BreakTypesEnum.Lunch,
                BreakTypesEnum.Refuel,
                BreakTypesEnum.Night
            ];
        }

        handleTabChange = index => {
            this.setState({ activeTabIndex: index });
        };

        onBreakValueChange = ({
            fieldId,
            breakType,
            breakIndex,
            value,
            lat,
            lng,
            description,
            offset
        }) => {
            const { getUpdatedBreakValue } = this.props;
            this.setState(prevState => {
                let { breaks } = prevState;
                const breaksList = prevState.breaks[breakType.toLowerCase()];
                const selectedBreak = breaksList[breakIndex];
                const updatedBreak = getUpdatedBreakValue({
                    fieldId,
                    value,
                    lat,
                    lng,
                    description,
                    selectedBreak,
                    offset
                });
                const updatedBreaksList = ArrayUtil.insertItemAtIndex({
                    array: breaksList,
                    index: breakIndex,
                    item: updatedBreak
                });
                breaks = {
                    ...breaks,
                    [breakType.toLowerCase()]: updatedBreaksList
                };
                return {
                    breaks
                };
            });
        };

        addBreak = breakType => {
            const { getNewBreak, getUpdatedBreaks } = this.props;
            this.setState(
                ({
                    breaks: prevBreaks,
                    unappliedBreaks: prevUnappliedBreaks
                }) => {
                    const unappliedBreaks = this.updateUnappliedBreaks({
                        prevUnappliedBreaks,
                        prevBreaks,
                        breakType
                    });
                    const newBreak = getNewBreak({ breakType });
                    const breaks = getUpdatedBreaks({
                        breaks: prevBreaks,
                        breakType,
                        newBreak
                    });
                    return { breaks, unappliedBreaks };
                }
            );
        };

        updateUnappliedBreaks = ({
            prevUnappliedBreaks,
            prevBreaks,
            breakType
        }) => {
            const { getBreakKey } = this.props;
            let unappliedBreaks = prevUnappliedBreaks;
            const key = getBreakKey({
                breakType,
                breakIndex: prevBreaks[breakType.toLowerCase()].length
            });
            if (prevUnappliedBreaks.indexOf(key) === -1) {
                unappliedBreaks = [...unappliedBreaks, key];
            }
            return unappliedBreaks;
        };

        toggleApplyToAll = () => {
            this.setState({ applyToAll: !this.state.applyToAll });
        };

        deleteBreak = (breakType, breakKey, index) => {
            const { breaks, unappliedBreaks } = this.state;
            let newUnappliedBreaks = unappliedBreaks;
            const breaksList = breaks[breakType.toLowerCase()];
            if (unappliedBreaks.indexOf(breakKey) >= 0) {
                newUnappliedBreaks = newUnappliedBreaks.filter(
                    key => key === breakKey
                );
            }
            const updatedBreaksList = ArrayUtil.removeItemAtIndex({
                array: breaksList,
                index
            });
            this.setState({
                breaks: {
                    ...breaks,
                    [breakType.toLowerCase()]: updatedBreaksList
                },
                unappliedBreaks: newUnappliedBreaks
            });
        };

        onAccept = () => {
            const { breaks, applyToAll } = this.state;
            this.props.onAcceptBreaks({
                breaks,
                applyToAll
            });
        };

        handleFieldFocus = key => {
            this.setState(previousState => {
                return previousState.activeBreak !== key
                    ? { activeBreak: key }
                    : {};
            });
        };

        render() {
            const {
                theme,
                date,
                applyToAllLabel,
                closeBreaksDialog,
                showApplyToAll,
                homebases,
                t,
                getBreakKey,
                breakSlotFor,
                shiftStart,
                shiftEnd,
                endDayOffset,
                isBreaksWithOffset
            } = this.props;
            const {
                activeBreak,
                unappliedBreaks,
                breaks,
                applyToAll,
                activeTabIndex
            } = this.state;
            return (
                <BreaksDialogComponent
                    active
                    title={t('applyBreaks')}
                    theme={theme}
                    className={theme.breakDialogOverlay}
                    dialogClassName={theme.breakDialog}
                    breakTypeList={this.breakTypeList}
                    currentDate={date}
                    applyToAllLabel={applyToAllLabel}
                    showApplyToAll={showApplyToAll}
                    homebases={homebases}
                    activeTabIndex={activeTabIndex}
                    allBreaks={breaks}
                    applyToAll={applyToAll}
                    unappliedBreaks={unappliedBreaks}
                    activeBreakKey={activeBreak}
                    toggleApplyToAll={this.toggleApplyToAll}
                    getBreakKey={getBreakKey}
                    onTabChange={this.handleTabChange}
                    addBreak={this.addBreak}
                    deleteBreak={this.deleteBreak}
                    onBreakValueChange={this.onBreakValueChange}
                    handleFieldFocus={this.handleFieldFocus}
                    onDismiss={closeBreaksDialog}
                    onAccept={this.onAccept}
                    breakSlotFor={breakSlotFor}
                    shiftStart={shiftStart}
                    shiftEnd={shiftEnd}
                    endDayOffset={endDayOffset}
                    isBreaksWithOffset={isBreaksWithOffset}
                />
            );
        }
    }
    return themr('BreaksDialog', defaultTheme)(BreaksDialog);
};
export { breaksDialogFactory };
export default breaksDialogFactory;
